import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { LimitWarning } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { Plus, Pencil, Trash2, Users, Search, Phone, Mail, Factory, MoreHorizontal, MapPin, Smartphone, Key } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import OperatorEditDrawer from '../components/OperatorEditDrawer';

const Operators = () => {
  const { api } = useAuth();
  const { checkLimit, refreshTierData, currentTier } = useTier();
  const [operators, setOperators] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);

  // Mobile PIN Dialog
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinOperator, setPinOperator] = useState(null);
  const [newPin, setNewPin] = useState('');
  const [newOperatorCode, setNewOperatorCode] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [operatorToDelete, setOperatorToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = async () => {
    try {
      const [opRes, suppRes, eqTypesRes] = await Promise.all([
        api.get('/operators'),
        api.get('/suppliers'),
        api.get('/equipment-types/all')
      ]);
      setOperators(opRes.data);
      setSuppliers(suppRes.data);
      setEquipmentTypes(eqTypesRes.data.filter(et => et.is_active !== false));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  // Auto-select supplier for Basic tier with single supplier
  const defaultSupplierId = useMemo(() => {
    if (currentTier === 'basic' && suppliers.length === 1) {
      return suppliers[0].id;
    }
    return null;
  }, [currentTier, suppliers]);

  const handleOpenDialog = (op = null) => {
    setEditingOperator(op);
    setDialogOpen(true);
  };

  const handleQuickToggleMobile = async (op, enabled) => {
    try {
      await api.put(`/operators/${op.id}`, {
        name: op.name,
        mobile_enabled: enabled,
        mobile_pin: enabled ? (op.mobile_pin || '') : op.mobile_pin,
        operator_code: enabled ? (op.operator_code || op.name.split(' ')[0].toUpperCase().slice(0, 2) + '001') : op.operator_code
      });
      toast.success(enabled ? 'Mobile access enabled' : 'Mobile access disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSubmit = async (formData, operator) => {
    try {
      if (operator) {
        await api.put(`/operators/${operator.id}`, formData);
        toast.success('Operator updated');
      } else {
        await api.post('/operators', formData);
        toast.success('Operator added');
        refreshTierData();
      }
      setDialogOpen(false);
      setEditingOperator(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    const operator = operators.find(o => o.id === id);
    setOperatorToDelete(operator);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!operatorToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/operators/${operatorToDelete.id}`);
      toast.success('Operator deleted');
      fetchData();
      refreshTierData();
      setDeleteDialogOpen(false);
      setOperatorToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateSupplier = async (supplierData) => {
    try {
      const response = await api.post('/suppliers', supplierData);
      toast.success('Supplier created');
      const suppRes = await api.get('/suppliers');
      setSuppliers(suppRes.data);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create supplier');
      return null;
    }
  };

  const handleOpenPinDialog = (op) => {
    setPinOperator(op);
    setNewPin(op.mobile_pin || '');
    setNewOperatorCode(op.operator_code || '');
    setPinDialogOpen(true);
  };

  const handleUpdatePin = async () => {
    if (!newPin || newPin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }
    if (!newOperatorCode) {
      toast.error('Operator code is required');
      return;
    }
    try {
      await api.put(`/operators/${pinOperator.id}`, {
        name: pinOperator.name,
        mobile_enabled: true,
        mobile_pin: newPin,
        operator_code: newOperatorCode
      });
      toast.success('Mobile credentials updated');
      setPinDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const filteredOperators = operators.filter(op =>
    op.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (op.operator_code && op.operator_code.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (op.email && op.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.company_name || '-';
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  // Check tier limit for adding new operators
  const limitCheck = checkLimit('operators', operators.length);

  return (
    <div className="space-y-6" data-testid="operators-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Operators</h1>
          <p className="text-muted-foreground">Crane operators and supervisors</p>
        </div>
        <Button 
          className="btn-volt" 
          onClick={() => handleOpenDialog()} 
          data-testid="add-operator-btn"
          disabled={!limitCheck.allowed}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Operator
        </Button>
      </div>

      {/* Limit Warning */}
      {!limitCheck.allowed && (
        <LimitWarning 
          resource="operators"
          current={operators.length}
          limit={limitCheck.limit}
        />
      )}

      {/* Shared Operator Edit Drawer */}
      <OperatorEditDrawer
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        operator={editingOperator}
        suppliers={suppliers}
        equipmentTypes={equipmentTypes}
        onSubmit={handleSubmit}
        onSupplierCreate={handleCreateSupplier}
        defaultSupplierId={defaultSupplierId}
      />

      {/* Mobile PIN Dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Key className="h-5 w-5 text-[#ffd108]" />
              Mobile App Credentials
            </DialogTitle>
            <DialogDescription>
              Set the operator code and PIN for {pinOperator?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Access Code *</Label>
              <Input
                value={newOperatorCode}
                onChange={(e) => setNewOperatorCode(e.target.value.toUpperCase())}
                placeholder="JH001"
                className="font-mono uppercase"
              />
              <p className="text-xs text-muted-foreground">Used to access operator portal</p>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Mobile PIN *</Label>
              <Input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                placeholder="4-6 digit PIN"
                maxLength={6}
              />
              <p className="text-xs text-muted-foreground">4-6 digit PIN for mobile app login</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPinDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" className="btn-volt" onClick={handleUpdatePin}>
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="operator-search"
                placeholder="Search operators..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredOperators.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No operators found</p>
              <Button 
                className="mt-4 btn-volt" 
                onClick={() => handleOpenDialog()}
                disabled={!limitCheck.allowed}
              >
                Add Your First Operator
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="label-overline">Name</TableHead>
                    <TableHead className="label-overline">Code</TableHead>
                    <TableHead className="label-overline">Supplier</TableHead>
                    <TableHead className="label-overline">Role</TableHead>
                    <TableHead className="label-overline">Contact</TableHead>
                    <TableHead className="label-overline">Location</TableHead>
                    <TableHead className="label-overline">Assets</TableHead>
                    <TableHead className="label-overline">Mobile</TableHead>
                    <TableHead className="label-overline text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOperators.map((op) => (
                    <TableRow 
                      key={op.id} 
                      data-testid={`operator-row-${op.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={(e) => {
                        if (e.target.closest('button')) return;
                        handleOpenDialog(op);
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{op.name}</span>
                          {!op.is_available && (
                            <Badge variant="secondary" className="text-xs">Unavailable</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {op.operator_code ? (
                          <Badge variant="outline" className="font-mono">{op.operator_code}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {op.supplier_id ? (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Factory className="h-3 w-3" />
                            {getSupplierName(op.supplier_id)}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs">Internal</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={op.role === 'supervisor' ? 'default' : 'secondary'}>
                          {op.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5 text-sm text-muted-foreground">
                          {op.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />{op.phone}
                            </div>
                          )}
                          {op.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />{op.email}
                            </div>
                          )}
                          {!op.phone && !op.email && '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {op.suburb || op.state ? (
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {[op.suburb, op.state].filter(Boolean).join(', ')}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {op.equipment_types?.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {op.equipment_types.slice(0, 2).map(type => (
                              <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
                            ))}
                            {op.equipment_types.length > 2 && (
                              <Badge variant="secondary" className="text-xs">+{op.equipment_types.length - 2}</Badge>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {op.mobile_enabled ? (
                          <Badge className="bg-green-500/20 text-green-600 hover:bg-green-500/30">
                            <Smartphone className="h-3 w-3 mr-1" />
                            Enabled
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            Disabled
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`operator-actions-${op.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); handleOpenDialog(op); }}
                              data-testid={`edit-operator-${op.id}`}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {op.mobile_enabled ? (
                              <>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenPinDialog(op); }}>
                                  <Key className="h-4 w-4 mr-2" />
                                  Change PIN
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleQuickToggleMobile(op, false); }}>
                                  <Smartphone className="h-4 w-4 mr-2" />
                                  Disable Mobile
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenPinDialog(op); }}>
                                <Smartphone className="h-4 w-4 mr-2" />
                                Enable Mobile
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); handleDelete(op.id); }}
                              className="text-destructive focus:text-destructive"
                              data-testid={`delete-operator-${op.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Operator"
        description={`Are you sure you want to delete "${operatorToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="delete-operator-dialog"
      />
    </div>
  );
};

export default Operators;
